import { buildProductPathFromIdentity } from '../../routing'
import type { CatalogProduct } from '../../types'
import type { ShopI18nContextValue } from '../../../i18n/provider/types'

type Props = {
  products: CatalogProduct[]
  loading: boolean
  toLocalizedPath: (path: string) => string
  formatCurrency: ShopI18nContextValue['formatCurrency']
}

const getProductImage = (product: CatalogProduct) =>
  product.mediaGallery?.[0] ||
  product.mediaVariants?.large ||
  product.mediaVariants?.medium ||
  product.mediaVariants?.small ||
  ''

const getProductPrice = (product: CatalogProduct) =>
  product.price

export const ProductLinkedProductsSection = ({
  products,
  loading,
  toLocalizedPath,
  formatCurrency,
}: Props) => {
  if (loading) {
    return (
      <section className="border-t border-slate-200 pt-8" aria-live="polite">
        <h2 className="text-2xl font-black tracking-tight text-slate-950">Accessori e prodotti compatibili</h2>
        <p className="mt-2 text-sm text-slate-600">Caricamento prodotti collegati...</p>
      </section>
    )
  }

  if (products.length === 0) return null

  return (
    <section className="border-t border-slate-200 pt-8" aria-labelledby="linked-products-title">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Completa l'acquisto</p>
          <h2 id="linked-products-title" className="text-2xl font-black tracking-tight text-slate-950">
            Accessori e prodotti compatibili
          </h2>
        </div>
        <p className="max-w-xl text-sm text-slate-600">
          Componenti, alternative o prodotti abbinati collegati a questa scheda.
        </p>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {products.map((product) => {
          const href = toLocalizedPath(buildProductPathFromIdentity({ publicCode: product.publicCode, slug: product.slug }))
          const imageUrl = getProductImage(product)
          const price = getProductPrice(product)
          return (
            <a
              key={product._id}
              href={href}
              className="group grid grid-cols-[96px_minmax(0,1fr)] gap-3 rounded-xl border border-slate-200 bg-white p-3 transition hover:border-cyan-300 hover:shadow-sm"
            >
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={product.name}
                  loading="lazy"
                  decoding="async"
                  fetchPriority="low"
                  className="h-24 w-24 rounded-lg object-cover"
                />
              ) : (
                <div className="grid h-24 w-24 place-items-center rounded-lg bg-slate-100 text-xl font-black text-slate-500">
                  {product.name.slice(0, 1).toUpperCase()}
                </div>
              )}
              <div className="min-w-0">
                <p className="line-clamp-2 text-sm font-bold text-slate-950 group-hover:text-cyan-800">{product.name}</p>
                <p className="mt-1 text-xs text-slate-500">{product.publicCode || product.slug}</p>
                {typeof price === 'number' ? (
                  <p className="mt-2 text-sm font-black text-slate-950">{formatCurrency(price)}</p>
                ) : null}
              </div>
            </a>
          )
        })}
      </div>
    </section>
  )
}
